import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, User, Loader2, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { OptionPills } from "@/components/OptionPills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#121316] border border-zinc-800 rounded-xl px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-all duration-300 focus:border-teal-500/70 focus:ring-2 focus:ring-teal-500/20";

const PROJECT_TYPES = ["Mobile App", "Website", "Web App", "Backend / API", "Not sure yet"];
const BUDGETS = ["Under $5k", "$5k – $15k", "$15k – $40k", "$40k+", "Not sure yet"];
const EMPTY = { name: "", email: "", message: "", project_type: "", budget: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!form.project_type || !form.budget) {
      toast.error("Please choose a project type and budget range.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm(EMPTY);
      setTouched(false);
    } catch (err) {
      toast.error("Something went wrong. Please email sales@btmtechdevelopments.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-[#17181c] border-t border-zinc-800/60">
      <div className="absolute -top-24 left-1/3 w-[420px] h-[420px] rounded-full bg-teal-500/8 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 lg:gap-20">
        <div>
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-teal-400">
              04 — Contact
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-50">
              Have an idea?
              <br />
              <span className="text-teal-400">Let's bring it to life.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-md">
              Tell us about your project — an app, a website, or something in between. We'll get
              back to you with next steps.
            </p>
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4" data-testid="contact-person">
                <span className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-400">
                  <User size={18} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">Trinity Robertson</p>
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Primary contact</p>
                </div>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <span className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-400">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">Calgary, Alberta</p>
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Canada</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-400">
                  <Mail size={18} />
                </span>
                <a
                  href="mailto:sales@btmtechdevelopments.com"
                  data-testid="contact-email-link"
                  className="group inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-100 hover:text-teal-300 transition-colors"
                >
                  sales@btmtechdevelopments.com
                  <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#1A1B20] border border-zinc-800 rounded-2xl p-8 sm:p-10 space-y-5"
          >
            <div>
              <label htmlFor="contact-name" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                type="text"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Email
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            <OptionPills
              label="Project type"
              options={PROJECT_TYPES}
              value={form.project_type}
              onChange={pick("project_type")}
              testId="contact-project-type"
              error={touched && !form.project_type}
            />
            <OptionPills
              label="Budget range (CAD)"
              options={BUDGETS}
              value={form.budget}
              onChange={pick("budget")}
              testId="contact-budget"
              error={touched && !form.budget}
            />
            <div>
              <label htmlFor="contact-message" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your idea…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 text-[#121316] font-semibold px-7 py-4 rounded-xl hover:bg-teal-400 hover:shadow-[0_0_32px_rgba(20,184,166,0.35)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending…
                </>
              ) : (
                "Send message"
              )}
            </button>
            <p className="text-xs text-zinc-600 leading-relaxed">
              By submitting, you agree to our{" "}
              <a href="/privacy" data-testid="contact-privacy-link" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                Privacy Policy
              </a>
              .
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
